import { SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import type { RankedPlaybackSource } from "./sourceRanking";
import {
  acceptSourceProgress,
  beginSourceGeneration,
  BeginSourceGenerationOptions,
  bindSourceSurface,
  createSourceGenerationState,
  isAuthoritativeSourceSurface,
  markSourcePlaying,
  prepareSourceTrackChange,
  recoverSourceGeneration,
  resetSourceGenerationScope,
  resolveSourceReady,
  retrySourceGeneration,
  selectSourceGeneration,
} from "./sourceGeneration";

/** Owns decoder generations, source recovery attempts, and resume handoff. */
export function usePlayerSourceGeneration(
  scopeKey: string,
  explicitSourceId?: string,
) {
  const stateRef = useRef(createSourceGenerationState(scopeKey, explicitSourceId));
  const [selectedSourceId, setSelectedSourceIdState] = useState(stateRef.current.selectedSourceId);
  const [surfaceEpoch, setSurfaceEpoch] = useState(stateRef.current.surfaceEpoch);

  const publish = useCallback((nextState: typeof stateRef.current) => {
    const previous = stateRef.current;
    stateRef.current = nextState;
    if (previous.selectedSourceId !== nextState.selectedSourceId) {
      setSelectedSourceIdState(nextState.selectedSourceId);
    }
    if (previous.surfaceEpoch !== nextState.surfaceEpoch) {
      setSurfaceEpoch(nextState.surfaceEpoch);
    }
  }, []);

  useEffect(() => {
    publish(resetSourceGenerationScope(stateRef.current, scopeKey, explicitSourceId));
  }, [explicitSourceId, publish, scopeKey]);

  const setSelectedSourceId = useCallback((value: SetStateAction<string>) => {
    const sourceId = typeof value === "function"
      ? value(stateRef.current.selectedSourceId)
      : value;
    publish(selectSourceGeneration(stateRef.current, sourceId));
  }, [publish]);

  const bindSurface = useCallback((uri?: string) => {
    const result = bindSourceSurface(stateRef.current, uri);
    stateRef.current = result.state;
    return result.identity;
  }, []);

  const isActiveSurface = useCallback(
    (identity: string) => isAuthoritativeSourceSurface(stateRef.current, identity),
    [],
  );

  const beginSource = useCallback((sourceId: string, options: BeginSourceGenerationOptions) => {
    publish(beginSourceGeneration(stateRef.current, sourceId, options));
  }, [publish]);

  const retry = useCallback((positionSeconds: number, continuePausedSession: boolean) => {
    publish(retrySourceGeneration(stateRef.current, positionSeconds, continuePausedSession));
  }, [publish]);

  const prepareTrackChange = useCallback((positionSeconds: number, onlyIfDecoderReady: boolean) => {
    const result = prepareSourceTrackChange(stateRef.current, positionSeconds, onlyIfDecoderReady);
    stateRef.current = result.state;
    return result.prepared;
  }, []);

  const ready = useCallback((uri: string | undefined, positionSeconds: number, initialResumeSeconds: number) => {
    const result = resolveSourceReady(stateRef.current, uri, positionSeconds, initialResumeSeconds);
    stateRef.current = result.state;
    return { seekTarget: result.seekTarget, continuePausedSession: result.continuePausedSession };
  }, []);

  const acceptProgress = useCallback((seconds: number) => {
    const result = acceptSourceProgress(stateRef.current, seconds);
    stateRef.current = result.state;
    return result.accepted;
  }, []);

  const markPlaying = useCallback(() => {
    stateRef.current = markSourcePlaying(stateRef.current);
  }, []);

  const recover = useCallback((
    currentSourceId: string | undefined,
    rankedSources: RankedPlaybackSource[],
    enabled: boolean,
  ) => {
    const result = recoverSourceGeneration(stateRef.current, currentSourceId, rankedSources, enabled);
    stateRef.current = result.state;
    return { candidate: result.candidate, exhausted: result.exhausted, stale: result.stale };
  }, []);

  return {
    selectedSourceId,
    setSelectedSourceId,
    surfaceEpoch,
    recovering: Boolean(stateRef.current.recoverySourceId),
    bindSurface,
    isActiveSurface,
    beginSource,
    retry,
    prepareTrackChange,
    ready,
    acceptProgress,
    markPlaying,
    recover,
  };
}
